

import React, {
    useEffect,
    useState,
} from "react";

import api from "../../api/api";

import {
    Box,
    Typography,
    CircularProgress,
    Grid,
    Divider,
    Card,
    CardContent,
    Chip,
    Stack,
    Alert,
    Button,
} from "@mui/material";

import {
    FaBook,
    FaMoneyBillWave,
    FaGraduationCap,
    FaLayerGroup,
    FaUsers,
    FaChalkboardTeacher,
    FaUserGraduate,
} from "react-icons/fa";

const TeacherSubjectData = () => {
    const [subjects, setSubjects] = useState([]);
    const [teacherInfo, setTeacherInfo] =
        useState(null);

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const fetchTeacherData = async () => {
        try {
            setLoading(true);
            setError("");

            const token =
                localStorage.getItem(
                    "access"
                );

            const response =
                await api.get(
                    "/api/users/teacher-subjects/",
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

            setSubjects(
                Array.isArray(
                    response.data?.subjects
                )
                    ? response.data.subjects
                    : []
            );

            setTeacherInfo(
                response.data
                    ?.teacher_info || null
            );
        } catch (err) {
            console.error(
                "Error fetching teacher information:",
                err
            );

            setError(
                err?.response?.data
                    ?.detail ||
                    "Failed to fetch teacher information."
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTeacherData();
    }, []);

    if (loading) {
        return (
            <Box
                sx={{
                    minHeight: 380,
                    display: "flex",
                    alignItems: "center",
                    justifyContent:
                        "center",
                    px: 2,
                }}
            >
                <Stack
                    spacing={1.5}
                    alignItems="center"
                >
                    <Box
                        sx={{
                            width: 54,
                            height: 54,
                            borderRadius: 3,
                            display: "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "center",
                            background:
                                "#eff6ff",
                        }}
                    >
                        <CircularProgress
                            size={26}
                            thickness={4}
                        />
                    </Box>

                    <Typography
                        sx={{
                            color: "#172033",
                            fontWeight: 800,
                            fontSize:
                                "0.95rem",
                        }}
                    >
                        Loading teacher information
                    </Typography>

                    <Typography
                        sx={{
                            color: "#64748b",
                            fontSize:
                                "0.78rem",
                        }}
                    >
                        Preparing your assigned
                        subjects and classes...
                    </Typography>
                </Stack>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert
                severity="error"
                sx={{
                    borderRadius: 3,
                    fontSize: "0.85rem",
                }}
                action={
                    <Button
                        color="inherit"
                        size="small"
                        onClick={
                            fetchTeacherData
                        }
                    >
                        Retry
                    </Button>
                }
            >
                {error}
            </Alert>
        );
    }

    const divisionData =
        Array.isArray(
            teacherInfo?.division_data
        )
            ? teacherInfo.division_data
            : [];

    const divisionNames =
        Array.isArray(
            teacherInfo?.divisions
        )
            ? teacherInfo.divisions
            : [];

    const assignedDivisions =
        divisionData.length > 0
            ? divisionData
            : divisionNames.map(
                  (
                      division,
                      index
                  ) => ({
                      id: index,
                      name: division,
                  })
              );

    const gradeClasses =
        Array.isArray(
            teacherInfo?.grade_class
        )
            ? teacherInfo.grade_class
            : [];

    const InfoItem = ({
        icon,
        label,
        children,
        color = "#1677ff",
        background = "#eff6ff",
        fullWidth = false,
    }) => (
        <Grid
            item
            xs={12}
            sm={fullWidth ? 12 : 6}
        >
            <Box
                sx={{
                    height: "100%",
                    minHeight: 92,
                    p: {
                        xs: 1.5,
                        sm: 1.7,
                    },
                    borderRadius: 2.5,
                    border:
                        "1px solid #e2e8f0",
                    background:
                        "#ffffff",
                    transition:
                        "all .2s ease",
                    "&:hover": {
                        borderColor:
                            `${color}45`,
                        boxShadow:
                            "0 7px 18px rgba(15,23,42,.05)",
                    },
                }}
            >
                <Stack
                    direction="row"
                    spacing={1.2}
                    alignItems="flex-start"
                >
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            minWidth: 36,
                            borderRadius: 2,
                            display:
                                "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "center",
                            background,
                            color,
                            fontSize: 14,
                        }}
                    >
                        {icon}
                    </Box>

                    <Box
                        sx={{
                            minWidth: 0,
                            flex: 1,
                        }}
                    >
                        <Typography
                            sx={{
                                color:
                                    "#64748b",
                                fontSize:
                                    "0.72rem",
                                fontWeight: 750,
                                lineHeight: 1.3,
                                mb: 0.55,
                            }}
                        >
                            {label}
                        </Typography>

                        {children}
                    </Box>
                </Stack>
            </Box>
        </Grid>
    );

    return (
        <Box
            sx={{
                width: "100%",
            }}
        >
            {/* =====================================================
                TEACHER PROFILE
            ====================================================== */}

            {teacherInfo && (
                <Card
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        mb: 2.5,
                        border:
                            "1px solid #e2e8f0",
                        background:
                            "#ffffff",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            px: {
                                xs: 1.8,
                                sm: 2.2,
                            },
                            py: 1.8,
                            background:
                                "linear-gradient(135deg,#1677ff,#4f46e5)",
                            color: "#ffffff",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.2}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2.2,
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    background:
                                        "rgba(255,255,255,.15)",
                                }}
                            >
                                <FaChalkboardTeacher
                                    size={17}
                                />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        fontWeight:
                                            850,
                                        fontSize:
                                            "1rem",
                                    }}
                                >
                                    Teacher Profile
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.2,
                                        color:
                                            "rgba(255,255,255,.78)",
                                        fontSize:
                                            "0.73rem",
                                    }}
                                >
                                    Academic assignment
                                    information
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    <CardContent
                        sx={{
                            p: {
                                xs: 1.5,
                                sm: 2,
                            },
                            "&:last-child": {
                                pb: {
                                    xs: 1.5,
                                    sm: 2,
                                },
                            },
                        }}
                    >
                        <Grid
                            container
                            spacing={1.3}
                        >
                            <InfoItem
                                icon={
                                    <FaGraduationCap />
                                }
                                label="Qualification"
                                color="#2563eb"
                                background="#eff6ff"
                            >
                                <Typography
                                    sx={{
                                        color:
                                            "#172033",
                                        fontWeight:
                                            800,
                                        fontSize:
                                            "0.88rem",
                                        lineHeight:
                                            1.45,
                                        wordBreak:
                                            "break-word",
                                    }}
                                >
                                    {teacherInfo.qualification ||
                                        "Not provided"}
                                </Typography>
                            </InfoItem>

                            <InfoItem
                                icon={
                                    <FaMoneyBillWave />
                                }
                                label="Salary"
                                color="#16a34a"
                                background="#ecfdf3"
                            >
                                <Typography
                                    sx={{
                                        color:
                                            "#15803d",
                                        fontWeight:
                                            850,
                                        fontSize:
                                            "0.95rem",
                                    }}
                                >
                                    $
                                    {Number(
                                        teacherInfo.salary ||
                                            0
                                    ).toLocaleString()}
                                </Typography>
                            </InfoItem>

                            <InfoItem
                                icon={
                                    <FaUserGraduate />
                                }
                                label="Dependent Student"
                                color="#7c3aed"
                                background="#f5f3ff"
                            >
                                <Typography
                                    sx={{
                                        color:
                                            "#172033",
                                        fontWeight:
                                            800,
                                        fontSize:
                                            "0.88rem",
                                        lineHeight:
                                            1.4,
                                        wordBreak:
                                            "break-word",
                                    }}
                                >
                                    {teacherInfo.dependent_student ||
                                        "None"}
                                </Typography>
                            </InfoItem>

                            <InfoItem
                                icon={
                                    <FaLayerGroup />
                                }
                                label="Assigned Divisions"
                                color="#0891b2"
                                background="#ecfeff"
                            >
                                {assignedDivisions.length >
                                0 ? (
                                    <Box
                                        sx={{
                                            display:
                                                "flex",
                                            gap: 0.6,
                                            flexWrap:
                                                "wrap",
                                        }}
                                    >
                                        {assignedDivisions.map(
                                            (
                                                division,
                                                index
                                            ) => (
                                                <Chip
                                                    key={
                                                        division.id ??
                                                        index
                                                    }
                                                    label={
                                                        division.name ??
                                                        division
                                                    }
                                                    size="small"
                                                    sx={{
                                                        height: 27,
                                                        borderRadius:
                                                            1.5,
                                                        background:
                                                            "#ecfeff",
                                                        border:
                                                            "1px solid #a5f3fc",
                                                        color:
                                                            "#0e7490",
                                                        fontWeight:
                                                            750,
                                                        fontSize:
                                                            "0.7rem",
                                                    }}
                                                />
                                            )
                                        )}
                                    </Box>
                                ) : (
                                    <Typography
                                        sx={{
                                            color:
                                                "#94a3b8",
                                            fontSize:
                                                "0.8rem",
                                        }}
                                    >
                                        No divisions
                                        assigned
                                    </Typography>
                                )}
                            </InfoItem>

                            <Grid
                                item
                                xs={12}
                            >
                                <Box
                                    sx={{
                                        p: {
                                            xs: 1.5,
                                            sm: 1.7,
                                        },
                                        borderRadius:
                                            2.5,
                                        border:
                                            "1px solid #e2e8f0",
                                        background:
                                            "#ffffff",
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1.2}
                                        alignItems="flex-start"
                                    >
                                        <Box
                                            sx={{
                                                width: 36,
                                                height: 36,
                                                minWidth: 36,
                                                borderRadius: 2,
                                                display:
                                                    "flex",
                                                alignItems:
                                                    "center",
                                                justifyContent:
                                                    "center",
                                                background:
                                                    "#fff7ed",
                                                color:
                                                    "#ea580c",
                                            }}
                                        >
                                            <FaLayerGroup />
                                        </Box>

                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                flex: 1,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    color:
                                                        "#64748b",
                                                    fontSize:
                                                        "0.72rem",
                                                    fontWeight:
                                                        750,
                                                    mb: 0.8,
                                                }}
                                            >
                                                Grade Classes
                                            </Typography>

                                            {gradeClasses.length >
                                            0 ? (
                                                <Box
                                                    sx={{
                                                        display:
                                                            "flex",
                                                        gap: 0.65,
                                                        flexWrap:
                                                            "wrap",
                                                    }}
                                                >
                                                    {gradeClasses.map(
                                                        (
                                                            grade,
                                                            index
                                                        ) => (
                                                            <Chip
                                                                key={
                                                                    index
                                                                }
                                                                label={
                                                                    grade
                                                                }
                                                                size="small"
                                                                sx={{
                                                                    height: 28,
                                                                    borderRadius:
                                                                        1.5,
                                                                    background:
                                                                        "#eff6ff",
                                                                    color:
                                                                        "#1d4ed8",
                                                                    border:
                                                                        "1px solid #bfdbfe",
                                                                    fontWeight:
                                                                        750,
                                                                    fontSize:
                                                                        "0.7rem",
                                                                }}
                                                            />
                                                        )
                                                    )}
                                                </Box>
                                            ) : (
                                                <Typography
                                                    sx={{
                                                        color:
                                                            "#94a3b8",
                                                        fontSize:
                                                            "0.8rem",
                                                    }}
                                                >
                                                    No classes
                                                    assigned
                                                </Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* =====================================================
                SUBJECTS
            ====================================================== */}

            <Card
                elevation={0}
                sx={{
                    borderRadius: 3,
                    border:
                        "1px solid #e2e8f0",
                    background:
                        "#ffffff",
                    overflow: "hidden",
                }}
            >
                <CardContent
                    sx={{
                        p: {
                            xs: 1.5,
                            sm: 2,
                        },
                        "&:last-child": {
                            pb: {
                                xs: 1.5,
                                sm: 2,
                            },
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems:
                                "center",
                            justifyContent:
                                "space-between",
                            gap: 1,
                            mb: 1.8,
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1.1}
                            alignItems="center"
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2.2,
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    background:
                                        "#eaf3ff",
                                    color:
                                        "#1677ff",
                                }}
                            >
                                <FaBook />
                            </Box>

                            <Box>
                                <Typography
                                    sx={{
                                        color:
                                            "#172033",
                                        fontWeight:
                                            850,
                                        fontSize:
                                            "1rem",
                                    }}
                                >
                                    Assigned Subjects
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 0.2,
                                        color:
                                            "#64748b",
                                        fontSize:
                                            "0.73rem",
                                    }}
                                >
                                    Subjects available in
                                    your teaching profile
                                </Typography>
                            </Box>
                        </Stack>

                        <Chip
                            label={`${subjects.length} ${
                                subjects.length ===
                                1
                                    ? "Subject"
                                    : "Subjects"
                            }`}
                            size="small"
                            sx={{
                                borderRadius: 2,
                                background:
                                    "#f1f5f9",
                                color:
                                    "#475569",
                                fontWeight:
                                    800,
                                fontSize:
                                    "0.7rem",
                            }}
                        />
                    </Box>

                    <Divider
                        sx={{
                            mb: 1.8,
                            borderColor:
                                "#eef2f7",
                        }}
                    />

                    {subjects.length === 0 ? (
                        <Box
                            sx={{
                                py: 5,
                                textAlign:
                                    "center",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 58,
                                    height: 58,
                                    mx: "auto",
                                    mb: 1.5,
                                    borderRadius: 3,
                                    display:
                                        "flex",
                                    alignItems:
                                        "center",
                                    justifyContent:
                                        "center",
                                    background:
                                        "#f8fafc",
                                    color:
                                        "#94a3b8",
                                }}
                            >
                                <FaBook
                                    size={22}
                                />
                            </Box>

                            <Typography
                                sx={{
                                    color:
                                        "#334155",
                                    fontWeight:
                                        800,
                                    fontSize:
                                        "0.9rem",
                                }}
                            >
                                No subjects assigned
                            </Typography>

                            <Typography
                                sx={{
                                    mt: 0.5,
                                    color:
                                        "#94a3b8",
                                    fontSize:
                                        "0.76rem",
                                }}
                            >
                                Your assigned subjects
                                will appear here.
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: 430,
                                overflowY:
                                    "auto",
                                pr: 0.5,

                                "&::-webkit-scrollbar":
                                    {
                                        width: 5,
                                    },

                                "&::-webkit-scrollbar-track":
                                    {
                                        background:
                                            "transparent",
                                    },

                                "&::-webkit-scrollbar-thumb":
                                    {
                                        background:
                                            "#cbd5e1",
                                        borderRadius:
                                            10,
                                    },
                            }}
                        >
                            <Grid
                                container
                                spacing={1.2}
                            >
                                {subjects.map(
                                    (
                                        subject,
                                        index
                                    ) => (
                                        <Grid
                                            item
                                            xs={12}
                                            key={
                                                subject.id ??
                                                index
                                            }
                                        >
                                            <Box
                                                sx={{
                                                    p: {
                                                        xs: 1.35,
                                                        sm: 1.5,
                                                    },
                                                    borderRadius:
                                                        2.5,
                                                    border:
                                                        "1px solid #e2e8f0",
                                                    background:
                                                        "#ffffff",
                                                    display:
                                                        "flex",
                                                    alignItems:
                                                        "center",
                                                    gap: 1.3,
                                                    transition:
                                                        "all .2s ease",
                                                    "&:hover":
                                                        {
                                                            transform:
                                                                "translateY(-2px)",
                                                            borderColor:
                                                                "#bfdbfe",
                                                            background:
                                                                "#f8fbff",
                                                            boxShadow:
                                                                "0 8px 18px rgba(15,23,42,.06)",
                                                        },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        minWidth: 44,
                                                        borderRadius: 2.5,
                                                        display:
                                                            "flex",
                                                        alignItems:
                                                            "center",
                                                        justifyContent:
                                                            "center",
                                                        background:
                                                            "linear-gradient(135deg,#1677ff,#4f46e5)",
                                                        color:
                                                            "#ffffff",
                                                        boxShadow:
                                                            "0 7px 14px rgba(22,119,255,.18)",
                                                    }}
                                                >
                                                    <FaBook
                                                        size={
                                                            17
                                                        }
                                                    />
                                                </Box>

                                                <Box
                                                    sx={{
                                                        minWidth: 0,
                                                        flex: 1,
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            color:
                                                                "#172033",
                                                            fontSize:
                                                                {
                                                                    xs: "0.83rem",
                                                                    sm: "0.88rem",
                                                                },
                                                            fontWeight:
                                                                800,
                                                            lineHeight:
                                                                1.4,
                                                            wordBreak:
                                                                "break-word",
                                                        }}
                                                    >
                                                        {subject.name ||
                                                            "Unnamed Subject"}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            mt: 0.2,
                                                            color:
                                                                "#94a3b8",
                                                            fontSize:
                                                                "0.68rem",
                                                        }}
                                                    >
                                                        Assigned
                                                        teaching
                                                        subject
                                                    </Typography>
                                                </Box>

                                                <Chip
                                                    label="Assigned"
                                                    size="small"
                                                    sx={{
                                                        display:
                                                            {
                                                                xs: "none",
                                                                sm: "flex",
                                                            },
                                                        height: 25,
                                                        borderRadius:
                                                            1.5,
                                                        background:
                                                            "#ecfdf3",
                                                        color:
                                                            "#15803d",
                                                        fontWeight:
                                                            750,
                                                        fontSize:
                                                            "0.65rem",
                                                    }}
                                                />
                                            </Box>
                                        </Grid>
                                    )
                                )}
                            </Grid>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default TeacherSubjectData;
